import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
} from "recharts";
import {
  ArrowLeft,
  Brain,
  Play,
  ShieldCheck,
  Phone,
  ChevronRight,
  Sparkles,
  Users,
  Stethoscope,
  Beaker,
  FlaskConical,
  RefreshCcw,
  Trophy,
  BookOpen,
  Activity,
  LineChart as LineChartIcon,
  ClipboardCheck,
} from "lucide-react";
import {
  CATEGORIES,
  loadNeuro,
  saveNeuro,
  clearNeuro,
  recordExercise,
  recordBaseline,
  buildDailyPlan,
  aggregate,
  weeklyTrend,
  buildDemoState,
  beforeAfter,
  recoveryTimeline,
} from "@/lib/neuroStore";
import { EXERCISE_MAP } from "@/components/neurorehab/exercises";
import FunctionalExercises from "@/components/neurorehab/FunctionalExercises";
import SessionFeedbackModal from "@/components/neurorehab/SessionFeedbackModal";

const TABS = [
  { key: "welcome", label: "Welcome", Icon: Brain },
  { key: "baseline", label: "Baseline", Icon: Sparkles },
  { key: "train", label: "Today's Session", Icon: Play },
  { key: "functional", label: "Real-Life Training", Icon: ClipboardCheck },
  { key: "progress", label: "Progress", Icon: Trophy },
  { key: "before-after", label: "Before vs After", Icon: Activity },
  { key: "recovery-twin", label: "Recovery Twin", Icon: LineChartIcon },
  { key: "journey", label: "My Journey", Icon: ChevronRight },
  { key: "caregiver", label: "Caregiver", Icon: Users },
  { key: "therapist", label: "Professional", Icon: Stethoscope },
  { key: "research", label: "Research", Icon: FlaskConical },
  { key: "how", label: "How it works", Icon: BookOpen },
  { key: "demo", label: "2-min Demo", Icon: Beaker },
];

export default function NeuroRehab() {
  const [state, setState] = useState(() => loadNeuro());
  const [tab, setTab] = useState("welcome");
  const [demoActive, setDemoActive] = useState(false);
  const [feedback, setFeedback] = useState({ open: false, category: null, level: 1 });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tab]);

  const effectiveState = demoActive ? buildDemoState() : state;
  const refresh = () => setState(loadNeuro());
  const agg = useMemo(() => aggregate(effectiveState), [effectiveState]);
  const trend = useMemo(() => weeklyTrend(effectiveState), [effectiveState]);
  const beforeAfterRows = useMemo(() => beforeAfter(effectiveState), [effectiveState]);
  const timeline = useMemo(() => recoveryTimeline(effectiveState), [effectiveState]);

  const openFeedback = (category, level) =>
    setFeedback({ open: true, category, level });
  const closeFeedback = () => {
    setFeedback({ open: false, category: null, level: 1 });
    refresh();
  };

  return (
    <div
      className={`min-h-screen bg-[#05050A] text-white pb-24 ${
        state.profile?.accessibility?.large_text ? "text-lg" : ""
      } ${state.profile?.accessibility?.high_contrast ? "contrast-125" : ""}`}
    >
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 opacity-70"
        style={{
          background:
            "radial-gradient(45% 30% at 10% 0%, rgba(167,139,250,0.14) 0%, transparent 60%), radial-gradient(45% 30% at 95% 20%, rgba(0,229,255,0.15) 0%, transparent 60%)",
        }}
      />

      <div className="border-b border-white/5 sticky top-0 z-30 backdrop-blur-md bg-black/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <Link
            to="/"
            data-testid="neuro-back-home"
            className="inline-flex items-center gap-2 text-zinc-300 hover:text-white text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            SilentScan<span className="text-cyan-400">.AI</span>
          </Link>
          <a
            href="tel:108"
            data-testid="neuro-emergency-108"
            className="inline-flex items-center gap-1.5 rounded-full bg-red-500 text-black text-xs font-semibold px-3 py-1.5"
          >
            <Phone className="w-3.5 h-3.5" />
            Emergency 108
          </a>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pt-10">
        <div className="max-w-3xl">
          <div className="text-[10px] font-semibold uppercase tracking-[0.35em] text-violet-300/80">
            Chapter 14 · NeuroRehab AI
          </div>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl font-semibold leading-[1.02]">
            Assess. Personalize.{" "}
            <span className="italic text-violet-300">Adapt.</span>
          </h1>
          <p className="mt-5 text-zinc-300 max-w-xl text-lg leading-relaxed">
            An adaptive cognitive-rehabilitation support platform that measures
            performance, personalises exercises and tracks progress over time —
            for people recovering from stroke, brain injury or simply looking
            to sharpen daily function.
          </p>
        </div>

        {/* Safety banner — always visible */}
        <div
          data-testid="neuro-safety-banner"
          className="mt-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3"
        >
          <ShieldCheck className="w-4 h-4 text-amber-300 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-amber-100 leading-relaxed">
            NeuroRehab AI is an assistive rehabilitation <b>prototype</b> and
            does not replace a doctor, neurologist, psychologist, occupational
            therapist or rehabilitation professional. It does not diagnose or
            treat neurological conditions. Please stop the activity and contact
            your healthcare professional if you experience new, worsening or
            concerning symptoms.
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-8 border-b border-white/5 overflow-x-auto no-scrollbar">
          <div className="flex gap-1 min-w-max">
            {TABS.map((t) => (
              <button
                key={t.key}
                data-testid={`neuro-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2.5 text-sm inline-flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                  tab === t.key
                    ? "border-violet-400 text-white"
                    : "border-transparent text-zinc-400 hover:text-white"
                }`}
              >
                <t.Icon className="w-4 h-4" />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8">
          {tab === "welcome" && <WelcomePane state={state} refresh={refresh} onGo={setTab} />}
          {tab === "baseline" && <BaselinePane state={state} refresh={refresh} onDone={() => setTab("train")} />}
          {tab === "train" && (
            <TrainPane state={state} refresh={refresh} onFeedback={openFeedback} />
          )}
          {tab === "functional" && <FunctionalExercises level={2} />}
          {tab === "progress" && <ProgressPane agg={agg} trend={trend} state={effectiveState} demo={demoActive} />}
          {tab === "before-after" && <BeforeAfterPane rows={beforeAfterRows} demo={demoActive} />}
          {tab === "recovery-twin" && <RecoveryTwinPane timeline={timeline} demo={demoActive} />}
          {tab === "journey" && <JourneyPane state={effectiveState} />}
          {tab === "caregiver" && <CaregiverPane state={state} refresh={refresh} />}
          {tab === "therapist" && <TherapistPane state={effectiveState} />}
          {tab === "research" && <ResearchPane />}
          {tab === "how" && <HowItWorksPane />}
          {tab === "demo" && <DemoPane onEnter={() => { setDemoActive(true); setTab("progress"); }} onExit={() => setDemoActive(false)} active={demoActive} />}
        </div>
      </div>

      <SessionFeedbackModal
        open={feedback.open}
        category={feedback.category}
        level={feedback.level}
        onClose={closeFeedback}
      />
    </div>
  );
}

// ============================ PANES ================================

const WelcomePane = ({ state, refresh, onGo }) => {
  const patch = (k, v) => {
    const s = loadNeuro();
    s.profile = { ...s.profile, accessibility: { ...s.profile.accessibility, [k]: v } };
    saveNeuro(s);
    refresh();
  };
  const toggleConsent = () => {
    const s = loadNeuro();
    s.profile = { ...s.profile, consent_stored: !s.profile.consent_stored };
    saveNeuro(s);
    refresh();
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] gap-6">
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
        <div className="text-[10px] uppercase tracking-widest text-violet-300 font-semibold">
          The journey
        </div>
        <h2 className="mt-2 font-display text-3xl font-semibold">
          ASSESS → PERSONALIZE → TRAIN → ADAPT → MEASURE → TRACK
        </h2>
        <p className="mt-4 text-sm text-zinc-300 leading-relaxed">
          Start with a short baseline assessment. Based on your performance
          profile, we build a personalised daily training plan. Exercises adapt
          in real time — harder when you thrive, gentler when you struggle.
          Progress is tracked over time so you can see your own trajectory.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            data-testid="welcome-start-baseline"
            onClick={() => onGo("baseline")}
            className="inline-flex items-center gap-2 rounded-full bg-violet-400 text-black font-medium px-5 py-2.5 text-sm hover:bg-violet-300"
          >
            <Sparkles className="w-4 h-4" />
            {state.baseline ? "Redo baseline" : "Take baseline"}
          </button>
          <button
            data-testid="welcome-open-train"
            onClick={() => onGo("train")}
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-5 py-2.5 text-sm text-white"
          >
            <Play className="w-4 h-4" />
            Open today&apos;s session
          </button>
        </div>
      </div>
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
          Accessibility &amp; consent
        </div>
        <div className="mt-4 space-y-3">
          {[
            { k: "large_text", label: "Large text" },
            { k: "high_contrast", label: "High contrast" },
            { k: "audio_cues", label: "Audio cues (where available)" },
          ].map((row) => (
            <label
              key={row.k}
              data-testid={`neuro-a11y-${row.k}`}
              className="flex items-center justify-between rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 cursor-pointer"
            >
              <span className="text-sm text-zinc-200">{row.label}</span>
              <input
                type="checkbox"
                className="accent-violet-400 w-4 h-4"
                checked={!!state.profile?.accessibility?.[row.k]}
                onChange={(e) => patch(row.k, e.target.checked)}
              />
            </label>
          ))}
          <label
            data-testid="neuro-consent"
            className="flex items-start gap-3 rounded-xl border border-cyan-400/30 bg-cyan-400/5 px-3 py-2.5 cursor-pointer"
          >
            <input
              type="checkbox"
              className="accent-cyan-400 mt-0.5"
              checked={!!state.profile?.consent_stored}
              onChange={toggleConsent}
            />
            <span className="text-xs text-cyan-100 leading-relaxed">
              I consent to storing my exercise results on this device for
              personalised progress tracking. Nothing is uploaded without my
              explicit action (e.g., generating a caregiver share code).
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};

const BaselinePane = ({ state, refresh, onDone }) => {
  const [idx, setIdx] = useState(0);
  const [results, setResults] = useState({});
  const order = CATEGORIES.map((c) => c.key);
  if (idx >= order.length) {
    // finalize
    recordBaseline(results);
    refresh();
    return (
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
          Baseline complete
        </div>
        <h3 className="mt-2 font-display text-2xl font-semibold">
          Here is your performance profile.
        </h3>
        <p className="mt-2 text-sm text-zinc-400">
          These are <b>task-performance</b> results, not medical scores. We&apos;ll
          use them to personalise your training plan.
        </p>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
          {CATEGORIES.map((c) => {
            const r = results[c.key];
            const acc = r?.total ? Math.round((r.correct / r.total) * 100) : 0;
            return (
              <div
                key={c.key}
                className="rounded-2xl border border-white/10 bg-black/30 px-4 py-3 flex items-center justify-between"
              >
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-400 font-semibold">
                    {c.label}
                  </div>
                  <div className="text-xs text-zinc-500">
                    task performance
                  </div>
                </div>
                <div
                  className="font-display text-2xl font-semibold tabular-nums"
                  style={{ color: c.accent }}
                >
                  {acc}%
                </div>
              </div>
            );
          })}
        </div>
        <button
          data-testid="baseline-continue"
          onClick={onDone}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-violet-400 text-black font-medium px-5 py-2.5 text-sm hover:bg-violet-300"
        >
          Continue to training <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    );
  }
  const cat = order[idx];
  const Cmp = EXERCISE_MAP[cat];
  const label = CATEGORIES.find((c) => c.key === cat)?.label;
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-violet-300 font-semibold">
        Baseline · Step {idx + 1} of {order.length} — {label}
      </div>
      <div className="mt-4">
        <Cmp
          level={1}
          onDone={(r) => {
            setResults((prev) => ({ ...prev, [cat]: r }));
            setIdx((i) => i + 1);
          }}
        />
      </div>
    </div>
  );
};

const TrainPane = ({ state, refresh, onFeedback }) => {
  const plan = useMemo(() => buildDailyPlan(state), [state]);
  const [idx, setIdx] = useState(0);
  const [done, setDone] = useState(false);

  if (!state.baseline) {
    return (
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8 text-center">
        <Sparkles className="w-6 h-6 text-violet-300 mx-auto" />
        <div className="mt-3 font-display text-lg font-semibold">
          Please take the baseline first
        </div>
        <p className="mt-2 text-sm text-zinc-400 max-w-md mx-auto">
          Your training plan is personalised from your baseline task
          performance. It only takes about 5 minutes.
        </p>
      </div>
    );
  }

  if (done) {
    return (
      <div className="rounded-3xl border border-emerald-400/30 bg-emerald-400/5 p-8">
        <Trophy className="w-6 h-6 text-emerald-300" />
        <div className="mt-3 font-display text-2xl font-semibold">
          Session complete
        </div>
        <p className="mt-2 text-sm text-zinc-300">
          Well done. Your progress has been saved. Come back tomorrow — daily
          consistency is what shifts the trajectory.
        </p>
        <button
          data-testid="train-restart"
          onClick={() => {
            setIdx(0);
            setDone(false);
          }}
          className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-4 py-2 text-sm text-white"
        >
          <RefreshCcw className="w-3.5 h-3.5" />
          One more session
        </button>
      </div>
    );
  }

  const item = plan[idx];
  const Cmp = EXERCISE_MAP[item.category];
  const total = plan.length;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-violet-300 font-semibold">
            Today&apos;s session · {plan.reduce((a, b) => a + b.minutes, 0)} min
          </div>
          <h3 className="font-display text-2xl font-semibold">
            Exercise {idx + 1} of {total}
          </h3>
        </div>
        <div className="flex gap-1">
          {plan.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 w-8 rounded-full ${
                i < idx ? "bg-emerald-400" : i === idx ? "bg-violet-400" : "bg-white/10"
              }`}
            />
          ))}
        </div>
      </div>
      <Cmp
        level={item.level}
        onDone={(r) => {
          recordExercise(item.category, item.level, r.correct, r.total, r.reactionMs);
          refresh();
          onFeedback?.(item.category, item.level);
          if (idx + 1 >= total) setDone(true);
          else setIdx(idx + 1);
        }}
      />
    </div>
  );
};

const ProgressPane = ({ agg, trend, state, demo }) => (
  <div>
    {demo && (
      <div className="mb-4 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs uppercase tracking-widest text-amber-200 font-semibold text-center">
        Demo data — not clinical results
      </div>
    )}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {[
        { label: "Sessions", val: agg.total_sessions, color: "#22d3ee" },
        { label: "Exercises", val: agg.exercises_total, color: "#a78bfa" },
        { label: "Accuracy", val: `${agg.accuracy}%`, color: "#34d399" },
        { label: "Avg reaction", val: `${agg.reaction_ms} ms`, color: "#fbbf24" },
        { label: "Personal best", val: `${agg.personal_best}%`, color: "#f472b6" },
        { label: "Consistency", val: `${agg.consistency}%`, color: "#67e8f9" },
      ].map((s) => (
        <div key={s.label} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
            {s.label}
          </div>
          <div
            className="mt-1 font-display text-2xl font-semibold tabular-nums"
            style={{ color: s.color }}
          >
            {s.val}
          </div>
        </div>
      ))}
    </div>

    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
          Weekly accuracy trend
        </div>
        <div className="mt-4 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={trend} margin={{ top: 6, right: 10, left: -18, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="week" stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis domain={[0, 100]} stroke="rgba(255,255,255,0.35)" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} width={30} />
              <Tooltip contentStyle={{ background: "#0A0A14", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12, color: "#fff" }} />
              <Line type="monotone" dataKey="accuracy" stroke="#a78bfa" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
          Domain radar
        </div>
        <div className="mt-4 h-64">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart
              data={CATEGORIES.map((c) => ({
                subject: c.label,
                A: agg.per_category[c.key] || 0,
              }))}
            >
              <PolarGrid stroke="rgba(255,255,255,0.1)" />
              <PolarAngleAxis dataKey="subject" tick={{ fill: "#a1a1aa", fontSize: 10 }} />
              <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: "#71717a", fontSize: 9 }} />
              <Radar dataKey="A" stroke="#34d399" fill="#34d399" fillOpacity={0.3} />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  </div>
);

const JourneyPane = ({ state }) => {
  const steps = [
    { label: "Baseline", done: !!state.baseline },
    { label: "Training", done: (state.sessions || []).length > 0 },
    { label: "Retesting", done: (state.sessions || []).length >= 10 },
    { label: "Progress", done: (state.sessions || []).length >= 20 },
  ];
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
      <div className="text-[10px] uppercase tracking-widest text-violet-300 font-semibold">
        My cognitive journey
      </div>
      <h3 className="mt-2 font-display text-2xl font-semibold">
        Every consistent day adds up.
      </h3>
      <div className="mt-8 grid grid-cols-4 gap-4">
        {steps.map((s, i) => (
          <div key={s.label} className="text-center">
            <div
              className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center border-2 ${
                s.done
                  ? "bg-emerald-400/20 border-emerald-400 text-emerald-100"
                  : "bg-white/5 border-white/10 text-zinc-500"
              }`}
            >
              {s.done ? "✓" : i + 1}
            </div>
            <div className="mt-2 text-xs uppercase tracking-widest text-zinc-300">{s.label}</div>
          </div>
        ))}
      </div>
      <p className="mt-8 text-sm text-zinc-400 max-w-xl mx-auto text-center">
        You&apos;re comparing yourself only with your own past self. No other user
        is in this graph.
      </p>
    </div>
  );
};

const CaregiverPane = ({ state, refresh }) => {
  const grant = () => {
    const s = loadNeuro();
    s.profile.caregiver_share_code = Math.random().toString(36).slice(2, 8).toUpperCase();
    saveNeuro(s);
    refresh();
  };
  const revoke = () => {
    const s = loadNeuro();
    s.profile.caregiver_share_code = null;
    saveNeuro(s);
    refresh();
  };
  return (
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
      <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
        Caregiver mode
      </div>
      <h3 className="mt-2 font-display text-2xl font-semibold">Share progress with someone you trust.</h3>
      <p className="mt-2 text-sm text-zinc-400 max-w-xl">
        A caregiver can see completed sessions, exercise consistency, performance
        trends and missed sessions — <b>only</b> when you generate a share code
        below. Nothing is shared without your consent.
      </p>
      {state.profile?.caregiver_share_code ? (
        <div className="mt-6 rounded-2xl border border-emerald-400/30 bg-emerald-400/5 p-5">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
            Active share code
          </div>
          <div className="mt-2 font-display text-3xl font-semibold tabular-nums tracking-widest">
            {state.profile.caregiver_share_code}
          </div>
          <p className="mt-2 text-xs text-zinc-400">
            (Prototype — code is stored on this device. Backend sharing is a
            future upgrade.)
          </p>
          <button
            data-testid="caregiver-revoke"
            onClick={revoke}
            className="mt-4 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-4 py-2 text-xs text-white"
          >
            Revoke access
          </button>
        </div>
      ) : (
        <button
          data-testid="caregiver-grant"
          onClick={grant}
          className="mt-6 inline-flex items-center gap-2 rounded-full bg-cyan-500 text-black font-medium px-5 py-2.5 text-sm hover:bg-cyan-400"
        >
          <Users className="w-4 h-4" /> Generate share code
        </button>
      )}
    </div>
  );
};

const TherapistPane = ({ state }) => (
  <div>
    <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-3 text-[11px] text-amber-100 mb-4">
      PROTOTYPE VIEW · This is not a clinical decision support tool. No medical
      diagnoses or treatment recommendations are generated here.
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
          Recent sessions
        </div>
        <div className="mt-3 flex flex-col gap-2 max-h-72 overflow-y-auto pr-1">
          {(state.sessions || [])
            .slice(-15)
            .reverse()
            .map((s, i) => (
              <div
                key={i}
                className="flex items-center justify-between rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-xs"
              >
                <div>
                  <div className="text-white font-medium capitalize">{s.category}</div>
                  <div className="text-zinc-500">
                    {(s.at || "").slice(0, 16).replace("T", " ")} · L{s.level}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-white tabular-nums">
                    {s.correct}/{s.total}
                  </div>
                  <div className="text-zinc-500 tabular-nums">{s.reaction_ms} ms</div>
                </div>
              </div>
            ))}
          {(state.sessions || []).length === 0 && (
            <div className="text-xs text-zinc-500">No sessions yet.</div>
          )}
        </div>
      </div>
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
        <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
          Current difficulty levels
        </div>
        <div className="mt-3 flex flex-col gap-2">
          {CATEGORIES.map((c) => (
            <div
              key={c.key}
              className="flex items-center justify-between rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-xs"
            >
              <div className="text-zinc-300">{c.label}</div>
              <div
                className="font-display text-lg font-semibold tabular-nums"
                style={{ color: c.accent }}
              >
                L{state.levels?.[c.key] || 1}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const ResearchPane = () => (
  <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
    <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
      Research mode
    </div>
    <h3 className="mt-2 font-display text-2xl font-semibold">
      Can personalised adaptive cognitive exercises improve performance on
      selected tasks over repeated sessions?
    </h3>
    <p className="mt-3 text-sm text-zinc-400 max-w-2xl">
      This is a <b>research prototype</b>. Effectiveness will only be claimed
      when peer-reviewed evidence exists. Below are placeholders for the study
      design we intend to run.
    </p>
    <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
      {[
        ["Participants", "—"],
        ["Sessions per participant", "—"],
        ["Completion rate (avg)", "—"],
        ["Accuracy change (Δ)", "—"],
        ["Reaction-time change (Δ)", "—"],
        ["User feedback (avg /5)", "—"],
      ].map(([k, v]) => (
        <div key={k} className="rounded-2xl border border-white/10 bg-black/30 px-4 py-3">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
            {k}
          </div>
          <div className="mt-1 font-display text-2xl font-semibold text-zinc-400">{v}</div>
        </div>
      ))}
    </div>
    <div className="mt-6 text-xs text-zinc-400 leading-relaxed">
      <b>Known limitations of this prototype</b>: single-device localStorage
      results, no clinical validation, no medical outcome measures, small
      exercise pool, no adherence enforcement, no control group.
    </div>
  </div>
);

const HowItWorksPane = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
    {[
      { t: "Baseline assessment", b: "Five short interactive tasks measure memory, attention, processing speed, problem solving and executive function." },
      { t: "Exercise selection", b: "A rule-based scheduler rotates exercises across the five domains. No LLM invents exercises or treatments." },
      { t: "Adaptive difficulty", b: "After each round: ≥85% accuracy → level up; <50% → level down; otherwise → stay. Bounded 1-5." },
      { t: "Performance measurement", b: "We track correct/total, reaction time and consistency (days practised in the last 14)." },
      { t: "Progress tracking", b: "All data lives on your device. Charts compare you only with your own past self." },
      { t: "Safety", b: "Non-diagnostic language throughout, professional escalation guidance, emergency 108 always visible." },
    ].map((c) => (
      <div key={c.t} className="rounded-3xl border border-white/10 bg-white/[0.03] p-6">
        <div className="text-[10px] uppercase tracking-widest text-violet-300 font-semibold">
          {c.t}
        </div>
        <p className="mt-2 text-sm text-zinc-300 leading-relaxed">{c.b}</p>
      </div>
    ))}
  </div>
);

const DemoPane = ({ onEnter, onExit, active }) => (
  <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-8">
    <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">
      2-minute competition demo
    </div>
    <h3 className="mt-2 font-display text-2xl font-semibold">
      See a simulated 4-week journey in one click.
    </h3>
    <p className="mt-2 text-sm text-zinc-400 max-w-xl">
      Loads a simulated participant with a baseline, 28 days of sessions and a
      full progress dashboard. Data is clearly labelled as simulated.
    </p>
    <div className="mt-6 flex gap-3">
      {active ? (
        <button
          data-testid="demo-exit"
          onClick={onExit}
          className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 px-5 py-2.5 text-sm text-white"
        >
          Exit demo
        </button>
      ) : (
        <button
          data-testid="demo-start"
          onClick={onEnter}
          className="inline-flex items-center gap-2 rounded-full bg-amber-400 text-black font-medium px-5 py-2.5 text-sm hover:bg-amber-300"
        >
          <Play className="w-4 h-4" />
          Start 2-minute demo
        </button>
      )}
    </div>
    {active && (
      <div className="mt-4 rounded-2xl border border-amber-500/30 bg-amber-500/5 px-4 py-2 text-[11px] text-amber-100">
        Demo active — the Progress tab now shows simulated data. Return here
        and click Exit demo to see your own data.
      </div>
    )}
  </div>
);


// -----------------------------------------------------------------------
// Before vs After — baseline vs recent (last 7 days) accuracy per domain
// -----------------------------------------------------------------------
const BeforeAfterPane = ({ rows, demo }) => (
  <div>
    {demo && (
      <div className="mb-4 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs uppercase tracking-widest text-amber-200 font-semibold text-center">
        Demo data — not clinical results
      </div>
    )}
    <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
      <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">
        Before vs After
      </div>
      <h3 className="mt-2 font-display text-2xl sm:text-3xl font-semibold">
        Baseline vs the last 7 days.
      </h3>
      <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
        A domain-by-domain view of how your <b>task performance</b> has moved
        since your baseline. Delta is percentage-points — not a medical score.
      </p>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((r) => {
          const up = r.delta > 0;
          const flat = r.delta === 0;
          return (
            <div
              key={r.key}
              data-testid={`beforeafter-${r.key}`}
              className="rounded-2xl border border-white/10 bg-black/30 p-4"
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                    {r.label}
                  </div>
                  <div className="text-xs text-zinc-500">
                    {r.sample} exercises · last 7 days
                  </div>
                </div>
                <div
                  className={`text-[11px] uppercase tracking-widest font-semibold px-2 py-1 rounded-full border ${
                    up
                      ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-200"
                      : flat
                      ? "border-white/10 bg-white/5 text-zinc-300"
                      : "border-amber-400/40 bg-amber-400/10 text-amber-200"
                  }`}
                >
                  {r.delta > 0 ? "+" : ""}
                  {r.delta} pp
                </div>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <Stat label="Baseline" val={`${r.before}%`} color="#71717a" />
                <ChevronRight className="w-4 h-4 text-zinc-500" />
                <Stat label="Now" val={`${r.after}%`} color={r.accent} />
              </div>
              <div className="mt-3 h-2 rounded-full bg-white/5 overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.min(100, r.after)}%`,
                    background: r.accent,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 text-xs text-zinc-500 leading-relaxed max-w-2xl">
        <b>How to read this:</b> pp = percentage points. Positive delta means
        you got more items right per exercise than at baseline. Task-performance
        only. Small deltas are common and not a diagnosis of anything.
      </div>
    </div>
  </div>
);

const Stat = ({ label, val, color }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
      {label}
    </div>
    <div
      className="mt-0.5 font-display text-2xl font-semibold tabular-nums"
      style={{ color }}
    >
      {val}
    </div>
  </div>
);

// -----------------------------------------------------------------------
// Recovery Twin — longitudinal daily view of cognitive + functional + mood
// -----------------------------------------------------------------------
const RecoveryTwinPane = ({ timeline, demo }) => {
  const totalDays = timeline.length;
  const active = timeline.filter((d) => d.sessions > 0).length;
  const consistency = totalDays ? Math.round((active / totalDays) * 100) : 0;
  const cogAvg = (() => {
    const list = timeline.filter((d) => d.cognitive > 0);
    return list.length
      ? Math.round(list.reduce((a, d) => a + d.cognitive, 0) / list.length)
      : 0;
  })();
  const funAvg = (() => {
    const list = timeline.filter((d) => d.functional > 0);
    return list.length
      ? Math.round(list.reduce((a, d) => a + d.functional, 0) / list.length)
      : 0;
  })();
  const moodAvg = (() => {
    const list = timeline.filter((d) => d.mood > 0);
    return list.length
      ? Math.round(list.reduce((a, d) => a + d.mood, 0) / list.length)
      : 0;
  })();

  return (
    <div>
      {demo && (
        <div className="mb-4 rounded-2xl border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs uppercase tracking-widest text-amber-200 font-semibold text-center">
          Demo data — simulated participant
        </div>
      )}
      <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300 font-semibold">
          Recovery Twin
        </div>
        <h3 className="mt-2 font-display text-2xl sm:text-3xl font-semibold">
          Your last 14 days, at a glance.
        </h3>
        <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
          Three signals together: cognitive task-performance, real-life
          functional performance, and self-reported mood. Rule-based
          aggregation only. Not a diagnosis.
        </p>

        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Consistency", val: `${consistency}%`, color: "#67e8f9" },
            { label: "Cognitive avg", val: `${cogAvg}%`, color: "#a78bfa" },
            { label: "Functional avg", val: `${funAvg}%`, color: "#34d399" },
            { label: "Mood avg", val: `${moodAvg}%`, color: "#f472b6" },
          ].map((s) => (
            <div
              key={s.label}
              className="rounded-2xl border border-white/10 bg-black/30 p-4"
            >
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
                {s.label}
              </div>
              <div
                className="mt-1 font-display text-2xl font-semibold tabular-nums"
                style={{ color: s.color }}
              >
                {s.val}
              </div>
            </div>
          ))}
        </div>

        <div
          data-testid="recovery-timeline"
          className="mt-6 h-64 rounded-2xl border border-white/10 bg-black/30 p-4"
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={timeline}
              margin={{ top: 6, right: 10, left: -18, bottom: 0 }}
            >
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis
                dataKey="day"
                stroke="rgba(255,255,255,0.35)"
                tick={{ fontSize: 10 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                domain={[0, 100]}
                stroke="rgba(255,255,255,0.35)"
                tick={{ fontSize: 10 }}
                axisLine={false}
                tickLine={false}
                width={30}
              />
              <Tooltip
                contentStyle={{
                  background: "#0A0A14",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: 12,
                  fontSize: 12,
                  color: "#fff",
                }}
              />
              <Line
                type="monotone"
                dataKey="cognitive"
                stroke="#a78bfa"
                strokeWidth={2}
                dot={{ r: 2 }}
                name="Cognitive"
              />
              <Line
                type="monotone"
                dataKey="functional"
                stroke="#34d399"
                strokeWidth={2}
                dot={{ r: 2 }}
                name="Functional"
              />
              <Line
                type="monotone"
                dataKey="mood"
                stroke="#f472b6"
                strokeWidth={2}
                dot={{ r: 2 }}
                name="Mood"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-4 flex flex-wrap gap-4 text-xs text-zinc-400">
          <Legend color="#a78bfa" label="Cognitive (task accuracy)" />
          <Legend color="#34d399" label="Functional (real-life tasks)" />
          <Legend color="#f472b6" label="Mood (self-report 1-5)" />
        </div>
      </div>
    </div>
  );
};

const Legend = ({ color, label }) => (
  <div className="inline-flex items-center gap-2">
    <span
      className="inline-block w-2.5 h-2.5 rounded-full"
      style={{ background: color }}
    />
    {label}
  </div>
);
